/**
 * MapApp — wraps mapbox-gl Map instance + binds it to TileManager (BEACON-34 Phase 1)
 *
 * Renders an interactive satellite map of the active course. Phase 1A uses
 * Mapbox satellite-streets-v12. Phase 1B layers in Google Maps Tiles when
 * available.
 *
 * Course centering: course coords / camera defaults are pulled from
 * `golf/courses/<slug>/location.json` (single source of truth across PWA +
 * engine + tests). The build-time `scripts/sync-courses.sh` mirrors it into
 * `pwa/public/courses/<slug>/location.json`, fetched at runtime.
 *
 * Falls back to a hard-coded Crandon clubhouse default if the fetch fails,
 * but the fallback should never normally fire.
 */

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Fallback ONLY — used if /courses/<slug>/location.json fetch fails.
// Source of truth = golf/courses/crandon-golf/location.json.
// Crandon Golf clubhouse, 6700 Crandon Blvd, Key Biscayne, FL 33149
// (Google ROOFTOP geocode, place_id ChIJC_uG9o612YgR4-xHKDaSVBE).
const FALLBACK_CENTER = [-80.15925, 25.71639]; // [lon, lat]
const FALLBACK_ZOOM = 14.5;
const FALLBACK_PITCH = 30;

/**
 * Fetch a course's location.json (mirrored into /courses/<slug>/ at build time).
 * Returns a normalized {center, zoom, pitch, name, slug, holes} object.
 *
 * BEACON-53 (audit fix): `holes` surfaced from location.json so HolePicker /
 * BriefLoader stop hardcoding 18. Default = 18 (regulation) when missing.
 * Per-course truth: shorty's=19 (par-3 short course), bandon-preserve=13.
 */
export async function loadCourseLocation(slug) {
  const url = `/courses/${slug}/location.json`;
  try {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const loc = await res.json();
    const center = loc.map?.center || [loc.lng, loc.lat];
    const zoom = loc.map?.zoom ?? FALLBACK_ZOOM;
    const pitch = loc.map?.pitch ?? FALLBACK_PITCH;
    if (!Array.isArray(center) || center.length !== 2 || !Number.isFinite(center[0]) || !Number.isFinite(center[1])) {
      throw new Error(`Invalid center in ${url}: ${JSON.stringify(center)}`);
    }
    const holes = Number.isFinite(loc.holes) && loc.holes > 0 ? loc.holes : 18;
    return { center, zoom, pitch, name: loc.name || slug, slug, holes };
  } catch (err) {
    console.warn(`[MapApp] loadCourseLocation('${slug}') failed (${err}); using fallback`);
    return { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM, pitch: FALLBACK_PITCH, name: slug, slug, holes: 18 };
  }
}

export class MapApp {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.container - the div the map renders into
   * @param {import("../tiles/TileManager.js").TileManager} opts.tileManager
   * @param {string} opts.mapboxToken - Mapbox access token (required for mapbox-gl init)
   * @param {{center: [number, number], zoom: number, pitch: number}} [opts.initialView]
   *        Pre-resolved camera (from loadCourseLocation). Falls back to FALLBACK_*.
   */
  constructor({ container, tileManager, mapboxToken, initialView }) {
    this.container = container;
    this.tileManager = tileManager;
    this.mapboxToken = mapboxToken;
    this.map = null;
    this.courseViews = new Map(); // slug → {center, zoom, pitch}
    this.activeView = initialView || { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM, pitch: FALLBACK_PITCH };

    // React to source changes (online/offline, error fallback, etc.)
    this.tileManager.addEventListener("sourcechange", (e) => this._onSourceChange(e));
  }

  /**
   * Initialize the mapbox-gl map. Returns a Promise that resolves when the
   * map's first style has loaded.
   */
  async init() {
    if (!this.mapboxToken) {
      throw new Error("MapApp.init: mapboxToken required for mapbox-gl runtime");
    }
    mapboxgl.accessToken = this.mapboxToken;

    const style = this.tileManager.buildStyle();
    this.map = new mapboxgl.Map({
      container: this.container,
      style,
      center: this.activeView.center,
      zoom: this.activeView.zoom,
      pitch: this.activeView.pitch,
      attributionControl: true,
      cooperativeGestures: true, // friendlier on mobile (two-finger pan)
    });

    // Tile load lifecycle hooks for source health reporting
    this.map.on("data", (e) => {
      // Only flag mapbox tiles; google probe lives in TileManager Phase 1B
      if (e.dataType === "source" && e.tile) {
        this.tileManager.reportHealthy(this.tileManager.activeSource);
      }
    });
    this.map.on("error", (e) => {
      console.error("[MapApp] mapbox-gl error:", e?.error || e);
      // Mapbox errored — let TileManager decide whether to fall back
      this.tileManager.reportError(this.tileManager.activeSource, e?.error || "mapbox error");
    });

    // Wait for style ready
    await new Promise((resolve) => {
      if (this.map.isStyleLoaded()) return resolve();
      this.map.once("load", resolve);
    });

    // Lightweight nav controls (top-left)
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), "top-left");
    // Geolocate (Phase 3 will use this for hole detection)
    this.map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "top-left"
    );
  }

  _onSourceChange({ detail }) {
    if (!this.map) return;
    const newStyle = this.tileManager.buildStyle();
    console.info("[MapApp] applying new style:", typeof newStyle === "string" ? newStyle : "(inline)");
    this.map.setStyle(newStyle);
  }

  /** Re-center on a course slug. Pulls from `/courses/<slug>/location.json` if not already cached. */
  async centerOnCourse(slug) {
    if (!this.map) return;
    let view = this.courseViews.get(slug);
    if (!view) {
      view = await loadCourseLocation(slug);
      this.courseViews.set(slug, view);
    }
    this.activeView = view;
    this.map.flyTo({ center: view.center, zoom: view.zoom, pitch: view.pitch });
  }
}
